/* eslint-disable react-hooks/rules-of-hooks */
import React, { Fragment, useEffect, useState } from 'react';
import { Grid, Container, Typography, Tooltip } from '@mui/material';
import moment from 'moment';

interface Props {
  data: any;
  isOnLeave?:boolean;
  isCancellationRequest?: boolean;
}

const getInitials = (employee: any) => {
  const firstName = (employee?.firstName || '').trim();
  const lastName = (employee?.lastName || '').trim();

  if (firstName || lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }

  const parts = (employee?.name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return '';
  }

  const first = parts[0].charAt(0).toUpperCase();
  const last =
    parts.length > 1 ? parts[parts.length - 1].charAt(0).toUpperCase() : '';
  return `${first}${last}`;
};

const EmployeeCard = (props: Props) => {
  const employee =
    props?.data?.employeeDetailsDto || props?.data?.leaveRequester;
  const employeeId = employee?.employeeId;
  const imageUrl = employee?.url || employee?.image || employee?.profileImage || employee?.imageUrl;

  const resolvedImageUrl =
    imageUrl ||
    (employeeId
      ? `${process.env.REACT_APP_EMPLOYEE_MANAGEMENT_ENDPOINT}attachment/images-as-file/${employeeId}`
      : '');

  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [resolvedImageUrl]);

  const nameStyle = {
    fontSize: '13px',
    color: '#1e2a3b',
    fontWeight: 700,
    cursor: 'default',
    lineHeight: 1.3
  };

  const textStyle = {
    fontSize: '12px',
    color: '#8b95a7',
    fontWeight: 400,
    cursor: 'default',
    lineHeight: 1.3
  };

  const imageStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: props.isCancellationRequest ? '#E0B986' : 'white',
    objectFit: 'cover' as const
  };

  const dateWrapper = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexDirection: 'row',
  }

  const textStyleDate = {
    fontSize: '12px',
    color: '#8b95a7',
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
    marginLeft: '8px'
  };

  const hasImage = Boolean(resolvedImageUrl) && !imageFailed;
  const initials = getInitials(employee);

  return (
    <Fragment>
      <Grid
        container
        display={'flex'}
        alignItems={'flex-start'}
        wrap="nowrap"
        sx={{ width: '100%' }}
      >
        <Grid item>
          <div
            className={
              props.isCancellationRequest
                ? 'leave-card-cancel-avatar'
                : hasImage
                ? undefined
                : 'leave-card-initials'
            }
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: props.isCancellationRequest
                ? '#c9a24a'
                : hasImage
                ? 'white'
                : '#eef1fb',
              color: props.isCancellationRequest ? '#ffffff' : '#5c6ad2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0
            }}
          >
            {hasImage ? (
              <img
                style={imageStyle}
                src={resolvedImageUrl}
                alt="Profile"
                onError={() => setImageFailed(true)}
              />
            ) : (
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: 'inherit',
                  lineHeight: 1,
                  letterSpacing: '0.2px'
                }}
              >
                {initials}
              </span>
            )}
          </div>
        </Grid>
        <Grid item sx={{ marginLeft: '10px', minWidth: 0, flex: 1 }}>
          <Tooltip
            title={
              props?.data?.leaveRequester?.name
                ? props?.data?.leaveRequester?.name
                : props?.data?.employeeDetailsDto?.name
            }
            placement="bottom"
          >
            <Typography style={nameStyle} fontWeight="light" variant="h5">
              {props?.data?.employeeDetailsDto?.name
                ? props.data.employeeDetailsDto?.name.length > 20
                  ? props.data.employeeDetailsDto.name.slice(0, 18) + '...'
                  : props.data.employeeDetailsDto.name
                : props?.data?.leaveRequester?.name
                ? props?.data?.leaveRequester?.name.length > 20
                  ? props?.data?.leaveRequester?.name.slice(0, 18) + '---'
                  : props?.data?.leaveRequester?.name
                : '---'}
            </Typography>
          </Tooltip>
          <Grid sx={dateWrapper}>
            <Tooltip
              title={
                props?.data?.leaveRequester?.designation
                  ? props?.data?.leaveRequester?.designation
                  : props?.data?.employeeDetailsDto?.designation
              }
              placement="bottom"
            >
              <Typography style={textStyle} fontWeight="light" variant="h6">
                {props?.data?.employeeDetailsDto?.designation
                  ? props.data.employeeDetailsDto.designation.length > 21
                    ? props.data.employeeDetailsDto.designation.slice(0, 19) +
                      '...'
                    : props.data.employeeDetailsDto.designation
                  : props?.data?.leaveRequester?.designation
                  ? props?.data?.leaveRequester?.designation.length > 20
                    ? props?.data?.leaveRequester?.designation.slice(0, 18) +
                      '---'
                    : props?.data?.leaveRequester?.designation
                  : '---'}
              </Typography>
            </Tooltip>
          </Grid>
        </Grid>
        {!props.isOnLeave && (
          <Typography style={textStyleDate} fontWeight="light" variant="h6">
            {props.data?.leaveRequestDate
              ? moment(props.data?.leaveRequestDate).format('D MMM YYYY')
              : '---'}
          </Typography>
        )}
      </Grid>
    </Fragment>
  );
};

export default EmployeeCard;
